import React, { useEffect, useState } from 'react';
import { toInteger } from 'lodash';
import numeral from 'numeral';

import {
  Box,
  Container,
  makeStyles,
  Card,
  CardContent
} from '@material-ui/core';

import { MdMemory } from "react-icons/md";

import Page from 'src/components/Page';
import api from '../../services/api';

import Alerts from '../../utils/alerts';
import Confirmations from '../../utils/confirmations';

import Results from './Results';
import Toolbar from './Toolbar';
import Hardware from './Hardware';
import Create from './Create';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  }
}));

const defaultItem = {
  code: null,
  variant: null,
  name: null,
  image: null,
  platform: null,
  manufacturer: null,
  especifications: [],
  configs: []
}

const defaultDependencies = {
  loaded: false,
}

const defaultState = {
  loaded: false,
  records: 10,
  pages: 0,
  page: 0,
  count: 0,
  rows: []
}

const defaultStartDateTime = new Date();
const defaultEndDateTime = new Date();

const MODE_FULL = 0;
const MODE_COMPACT = 1;
const LEVEL_ACCESS_HIDE = -1;
const LEVEL_ACCESS_DISABLED = 0;
const LEVEL_ACCESS_ENABLED = 1;

const defaultResultColumns = [
  { name: 'Código', field: 'item.code', size: -1, sortable: 'name', exportable: 0, wrap: 1 },
  { name: 'Variante', field: 'item.variant', size: -1, sortable: null, exportable: 0, wrap: 1 },
  { name: 'Nome', field: 'item.name', size: -1, sortable: null, exportable: 0, wrap: 1 },
  { name: 'Plataforma', field: 'item.platform', size: -1, sortable: null, exportable: 0, wrap: 0 },
  { name: 'Fabricante', field: 'item.manufacturer', size: -1, sortable: null, exportable: 0, wrap: 1 },
  { name: 'Criação', field: 'formatDate(item.createdAt)', size: -1, sortable: 'createdAt', exportable: 0, wrap: 1 }
];

const defaultEmptyMessage = "Nenhum Hardware Foi Encontrado.";

const defaultfullMode = {
  mode: MODE_FULL,
  create: LEVEL_ACCESS_ENABLED,
  update: LEVEL_ACCESS_ENABLED,
  delete: LEVEL_ACCESS_ENABLED,
  config: LEVEL_ACCESS_HIDE,
  export: LEVEL_ACCESS_HIDE,
  empty: defaultEmptyMessage,
  columns: defaultResultColumns
};

const defaultCompactMode = {
  mode: MODE_COMPACT,
  create: LEVEL_ACCESS_ENABLED,
  update: LEVEL_ACCESS_ENABLED,
  delete: LEVEL_ACCESS_ENABLED,
  config: LEVEL_ACCESS_HIDE,
  export: LEVEL_ACCESS_HIDE,
  empty: defaultEmptyMessage,
  columns: defaultResultColumns
};

const defaultResultModes = [defaultfullMode, defaultCompactMode];

const defaultVisions = [
  { id: 1, name: 'Hardware pelo Nome', refine: 1, label: 'Nome do Hardware', history: 0 },
  { id: 2, name: 'Hardware pela Nome e Data de Criação', refine: 2, label: 'Nome do Hardware', history: 1 }
];

const defaultFilter = {
  pagina: 0,
  registros: 10,
  inicio: defaultStartDateTime,
  final: defaultEndDateTime,
  palavrachave: '',
  ordenacao: 'asc',
  ordem: '',
  visao: defaultVisions[0]
}

const Devices = () => {

  const classes = useStyles();

  const [item, setItem] = useState(defaultItem);
  const [openCreationDialog, setOpenCreationDialog] = useState(false);
  const [openEditionDialog, setOpenEditionDialog] = useState(false);
  const [openDeletionDialog, setOpenDeletionDialog] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [message, setMessage] = useState({});
  const [filter, setFilter] = useState(defaultFilter);
  const [vision, setVision] = useState(defaultVisions[0]);
  const [resultModes, setResultModes] = useState(defaultResultModes);
  const [selectedMode, setSelectedMode] = useState(MODE_FULL);
  const [dependencies, setDependencies] = useState(defaultDependencies);
  const [openTreatmentDialog, setOpenTreatmentDialog] = useState(false);

  const [state, setState] = useState(defaultState);

  const openExportDialog = async (format) => {
    setMessage({ error: true, warn: true, detail: "Função não implementada." });
    setOpenAlert(true);
  }

  const openEdition = async (item) => {
    setItem(item);
    setOpenEditionDialog(true);
  }

  const openCreation = async (item) => {
    setItem(item);
    setOpenCreationDialog(true);
  }

  const openDeletion = (item) => {
    setItem(item);
    setMessage({
      title: 'Hardware',
      message: 'Deseja excluir o hardware selecionado?',
      warn: 'Esta ação irá interferir na ativação de novos dispositivos.',
      details: ['Não será possível recuperar o hardware associado ao registrar um novo dispositivo.']
    });
    setOpenDeletionDialog(true);
  }

  const openTreatment = (item) => {
    setItem(item);
    setMessage({ error: true, warn: true, detail: "Função não implementada." });
    setOpenAlert(true);
  }

  const closeDeletion = () => {
    setOpenDeletionDialog(false);
  };

  const closeEdition = () => {
    setOpenEditionDialog(false);
  };

  const closeCreation = () => {
    setOpenCreationDialog(false);
  };

  const closeTreatment = () => {
    setOpenTreatmentDialog(false);
  };

  const closeAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenAlert(false);
  };

  const updateTreatmentValues = (values) => {
    setItem(values);
    refreshItems();
  }

  const updateValues = (values) => {
    api.put('/hardwares/' + values._id, values).then((response) => {
      if (!response.data.error) {
        refreshItems();
        closeEdition();
      }
      setMessage(response.data);
      setOpenAlert(true);
    }).catch((error) => {
      console.error(error);
      setMessage({ error: true, fatal: true, message: 'Erro ao atualizar hardware.', detail: error.message });
      setOpenAlert(true);
    })
  };

  const createValues = (values) => {
    api.post('/hardwares', values).then((response) => {
      if (!response.data.error) {
        refreshItems();
        closeCreation();
      }
      setMessage(response.data);
      setOpenAlert(true);
    }).catch((error) => {
      console.error(error);
      setMessage({ error: true, fatal: true, message: 'Erro ao criar hardware.', detail: error.message });
      setOpenAlert(true);
    })
  };

  const updateDependencies = async (values) => {

  };

  const deleteValues = (values) => {
    api.delete('/hardwares/' + values._id).then((response) => {
      if (!response.data.error) {
        refreshItems();
      }
      closeDeletion();
      setMessage(response.data);
      setOpenAlert(true);
    }).catch((error) => {
      console.error(error);
      setMessage({ error: true, fatal: true, message: 'Erro ao remover hardware.', detail: error.message });
      setOpenAlert(true);
    })
  };

  const changeSort = (column, order) => {
    var value = {
      ...filter,
      ordem: column.sortable,
      ordenacao: order,
      pagina: 0,
    };
    setFilter(value);
    api.post('/hardwares/history', { filtro: value }, {}).then((response) => {
      if (!response.data.error) {
        setState({
          loaded: true,
          pages: ((response.data.count > 0) ? toInteger((response.data.count / value.registros)) + 1 : 0),
          page: (response.data.count == 0) ? 0 : value.pagina + 1,
          count: response.data.count,
          rows: response.data.rows
        });
      } else {
        setState(defaultState);
        setMessage(response.data);
        setOpenAlert(true);
      }
    }).catch((error) => {
      console.error(error);
      setMessage({ error: true, fatal: true, message: 'Erro ao recuperar lista de hardwares.', detail: error.message });
      setOpenAlert(true);
    })
  }

  const changeFilter = (startDateTime, endDateTime, vision, keyword, page, rows) => {
    var value = {
      ...filter,
      pagina: page,
      registros: rows,
      inicio: startDateTime,
      final: endDateTime,
      palavrachave: keyword,
      visao: vision
    };
    setVision(vision);
    setFilter(value);
    api.post('/hardwares/history', { filtro: value }, {}).then((response) => {
      if (!response.data.error) {
        setState({
          loaded: true,
          pages: ((response.data.count > 0) ? toInteger((response.data.count / value.registros)) + 1 : 0),
          page: (response.data.count == 0) ? 0 : value.pagina + 1,
          count: response.data.count,
          rows: response.data.rows
        });
      } else {
        setState(defaultState);
        setMessage(response.data);
        setOpenAlert(true);
      }
    }).catch((error) => {
      console.error(error);
      setMessage({ error: true, fatal: true, message: 'Erro ao recuperar lista de hardwares.', detail: error.message });
      setOpenAlert(true);
    })
  }

  const refreshItems = () => {
    api.post('/hardwares/history', { filtro: filter }, {}).then((response) => {
      if (!response.data.error) {
        setState({
          loaded: true,
          pages: ((response.data.count > 0) ? toInteger((response.data.count / filter.registros)) + 1 : 0),
          page: (response.data.count == 0) ? 0 : filter.pagina + 1,
          count: response.data.count,
          rows: response.data.rows
        });
      } else {
        setState(defaultState);
        setMessage(response.data);
        setOpenAlert(true);
      }
    }).catch((error) => {
      console.error(error);
      setMessage({ error: true, fatal: true, message: 'Erro ao recuperar lista de hardwares.', detail: error.message });
      setOpenAlert(true);
    })
  }

  const clearImage = (values) => {
    setItem(values);
    values.image = null;
    openEdition(values);
  };

  useEffect(() => {
    if (!state.loaded) {
      refreshItems();
    }
    return
  }, []);

  return (
    <Page
      className={classes.root}
      title="Dispositivo"
    >
      <Container maxWidth={false}>

        <Toolbar
          icon={MdMemory}
          item={defaultItem}
          resultset={state}
          resultModes={resultModes}
          vision={vision}
          visions={defaultVisions}
          selectedMode={selectedMode}
          openCreation={openCreation}
          changeFilter={changeFilter}
        />

        <Alerts message={message}
          open={openAlert}
          closeAlert={closeAlert} />

        <Confirmations item={item}
          message={message}
          open={openDeletionDialog}
          close={closeDeletion}
          confirmation={deleteValues} />

        <Confirmations item={item}
          message={message}
          open={openTreatmentDialog}
          close={closeTreatment}
          confirmation={updateTreatmentValues} />

        <Hardware item={item}
          open={openEditionDialog}
          closeEdition={closeEdition}
          updateValues={updateValues}
          dependencies={dependencies}
          updateDependencies={updateDependencies}
          clearImage={clearImage}
        />

        <Create item={item}
          open={openCreationDialog}
          closeCreation={closeCreation}
          createValues={createValues}
          dependencies={dependencies}
          updateDependencies={updateDependencies}
        />

        <Box mt={3}>
          <Card>
            <CardContent>
              <Results
                resultSet={state}
                resultModes={resultModes}
                icon={MdMemory}
                vision={vision}
                selectedMode={selectedMode}
                openEdition={openEdition}
                openDeletion={openDeletion}
                openTreatment={openTreatment}
                openExportDialog={openExportDialog}
                changeSort={changeSort}
              />
            </CardContent>
          </Card>
        </Box>

      </Container>
    </Page>
  );
};

export default Devices;